/**
 * Five narrowings, every one of them a **server** parameter.
 *
 * `web/shapes/devices.md` §2. A client-side filter over one page of an offset-paged
 * endpoint silently answers "…among these fifty", which is not the question anybody
 * typed, and the count beside it would be wrong in a way nobody can see. So each
 * control here changes the request rather than the rendering, and the page resets to
 * offset 0 when it does — a filter applied at page three would show page three of a
 * list the reader has never seen page one of.
 *
 * **There is no liveness filter, and its absence is stated on screen.** `GET /devices`
 * offers `online=true|false`, and `false` "selects every other device, including those
 * that have never reported" — three of this screen's four readings folded into one
 * bucket inside the request itself, before the screen could decline to fold them. The
 * lifecycle `status` filter *is* offered, because that is the other axis and its enum
 * is the server's own.
 *
 * **Nhân sự and Phòng ban are pickers, not id boxes.** They were typed-id fields for
 * as long as Tasks 43 and 44 recorded; `EmployeePicker` (T2-B) and `DepartmentSelect`
 * (T2-A) replaced them, and this bar sends the id the picker resolved rather than one
 * a reader had to know by heart. The hints still say that an id here is a **filter**
 * and not a scope — the server intersects it with `WpScope.employeeIds`, so a
 * selection outside the caller's field of view narrows the answer or is refused, and
 * never widens it.
 */

import { useState, type ReactNode, type SyntheticEvent } from 'react';

import { useI18n } from '../../i18n';
import { DepartmentSelect } from '../../components/DepartmentSelect';
import { EmployeePicker, type EmployeePickerValue } from '../../components/EmployeePicker';
import { DEVICE_STATUSES, type DeviceStatus } from '../../api/types';
import type { MessageKey } from '../../i18n/messages';

const STATUS_LABEL: Readonly<Record<DeviceStatus, MessageKey>> = {
  pending: 'devices.status.pending',
  active: 'devices.status.active',
  offline: 'devices.status.offline',
  suspended: 'devices.status.suspended',
  revoked: 'devices.status.revoked',
  uninstalled: 'devices.status.uninstalled',
  agent_outdated: 'devices.status.agent_outdated',
  policy_error: 'devices.status.policy_error',
  sync_error: 'devices.status.sync_error',
};

export interface DeviceFilterValues {
  readonly status: DeviceStatus | null;
  readonly q: string | null;
  readonly employeeId: string | null;
  readonly departmentId: string | null;
}

export const NO_DEVICE_FILTERS: DeviceFilterValues = {
  status: null,
  q: null,
  employeeId: null,
  departmentId: null,
};

export function hasAnyFilter(values: DeviceFilterValues): boolean {
  return (
    values.status !== null ||
    values.q !== null ||
    values.employeeId !== null ||
    values.departmentId !== null
  );
}

function trimmedOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function DeviceFilters({
  values,
  onApply,
  onReload,
}: {
  readonly values: DeviceFilterValues;
  readonly onApply: (next: DeviceFilterValues) => void;
  readonly onReload: () => void;
}): ReactNode {
  const { t } = useI18n();
  const [status, setStatus] = useState<string>(values.status ?? '');
  const [q, setQ] = useState(values.q ?? '');
  // Restored from the URL: an achau id and no name, so the box shows the id until the
  // person picks a row. `achau_user_id` is set to it as well, because that — not
  // `id` — is what `submit` sends and what `/devices` resolves (R3).
  const [employee, setEmployee] = useState<EmployeePickerValue | null>(
    values.employeeId === null
      ? null
      : {
          id: values.employeeId,
          full_name: values.employeeId,
          achau_user_id: values.employeeId,
        },
  );
  const [departmentId, setDepartmentId] = useState<string | null>(values.departmentId);

  // `SyntheticEvent`, not `FormEvent`: React's own types deprecate the latter with the
  // note that it "doesn't actually exist", and `@typescript-eslint/no-deprecated`
  // has caught it three times on this build already.
  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    onApply({
      status: DEVICE_STATUSES.find((value) => value === status) ?? null,
      q: trimmedOrNull(q),
      // `achau_user_id`, not `id`: `GET /devices?employee_id=` takes the mirror's
      // identifier (`admin.ts`'s `resolveEmployeeInScope`), and sending ours filtered
      // by a number that means somebody else — or nobody (R3).
      employeeId: employee?.achau_user_id ?? null,
      departmentId,
    });
  };

  const clear = (): void => {
    setStatus('');
    setQ('');
    setEmployee(null);
    setDepartmentId(null);
    onApply(NO_DEVICE_FILTERS);
  };

  return (
    <form className="wp-c-bar" onSubmit={submit} data-testid="k5-filters">
      <fieldset className="wp-c-bar__group">
        <legend className="wp-c-bar__legend">{t('devices.filter.legend')}</legend>

        <label className="wp-field" htmlFor="k5-status">
          <span className="wp-field__label">{t('devices.filter.status')}</span>
          <select
            id="k5-status"
            className="wp-field__input"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
            }}
            data-testid="k5-status"
          >
            <option value="">{t('devices.filter.statusAny')}</option>
            {DEVICE_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(STATUS_LABEL[value])}
              </option>
            ))}
          </select>
        </label>

        <label className="wp-field" htmlFor="k5-q">
          <span className="wp-field__label">{t('devices.filter.q')}</span>
          <input
            id="k5-q"
            className="wp-field__input"
            value={q}
            aria-describedby="k5-q-hint"
            onChange={(event) => {
              setQ(event.target.value);
            }}
            data-testid="k5-q"
          />
        </label>
        <p className="wp-small wp-muted" id="k5-q-hint">
          {t('devices.filter.qHint')}
        </p>

        {/* Both were typed-id boxes under a line promising a picker "sẽ có sau".
            `EmployeePicker` (T2-B) and `DepartmentSelect` are those pickers, and
            they have been mounted for longer than the promise stood. The second
            `<fieldset>` went with them: its legend repeated the label of the field
            directly under it, which is the eyebrow the finish review deleted. */}
        <EmployeePicker
          label={t('devices.filter.employee')}
          testId="k5-employee"
          value={employee}
          onChange={setEmployee}
        />
        <p className="wp-small wp-muted" id="k5-employee-hint">
          {t('devices.filter.employeeHint')}
        </p>

        <DepartmentSelect
          id="k5-department"
          testId="k5-department"
          label={t('devices.filter.department')}
          value={departmentId}
          onChange={setDepartmentId}
        />
        <p className="wp-small wp-muted" id="k5-department-hint">
          {t('devices.filter.departmentHint')}
        </p>
      </fieldset>

      <div className="wp-c-actions">
        <button type="submit" className="wp-button wp-button--primary" data-testid="k5-apply">
          {t('devices.filter.apply')}
        </button>
        {hasAnyFilter(values) ? (
          <button
            type="button"
            className="wp-button wp-button--quiet"
            onClick={clear}
            data-testid="k5-clear"
          >
            {t('devices.filter.clear')}
          </button>
        ) : null}
        <button
          type="button"
          className="wp-button wp-button--quiet"
          onClick={onReload}
          data-testid="k5-reload"
        >
          {t('devices.filter.reload')}
        </button>
      </div>

      {/* The absent control, named. A reader who looks for an online/offline toggle
          and does not find one should learn why rather than conclude it was
          forgotten. */}
      <p className="wp-small wp-muted wp-c-bar__note" data-testid="k5-no-online-filter">
        {t('devices.filter.noOnline')}
      </p>
    </form>
  );
}
